import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Buildings, CurrencyInr } from "@phosphor-icons/react";

// Access is configured by the HOST only (owner decision 2026-09-07). Co-hosts and
// managers work inside the pages they were given and cannot grant access to anyone,
// so there is no grantor cascade here any more — the backend enforces owner-only too.

export const ALL_PAGES = [
  { key: "dashboard",     label: "Dashboard" },
  { key: "properties",    label: "Properties" },
  { key: "calendar",      label: "Bookings & Calendar" },
  { key: "inbox",         label: "Inbox" },
  { key: "tasks",         label: "Tasks" },
  { key: "expenses",      label: "Expenses" },
  { key: "pricing",       label: "Pricing" },
  { key: "evaluator",     label: "Evaluator" },
  { key: "finder",        label: "Finder" },
  { key: "gst",           label: "GST" },
  { key: "setup-advisor", label: "Setup Advisor" },
  { key: "legal",         label: "Legal Guide" },
  { key: "integrations",  label: "Integrations" },
  { key: "settings",      label: "Settings" },
];

const LEVELS = ["none", "read", "edit"];

export function roleBadge(role) {
  if (role === "co_host") return "Co-host";
  if (role === "manager") return "Manager";
  return "Maintenance";
}

/**
 * Shared access grid — used inside the Edit Access modal AND inline in the invite form.
 * Fully controlled: the parent owns permissions / showRevenue / assignedIds.
 */
export function AccessGrid({
  role, permissions, onPermissions, showRevenue, onShowRevenue,
  properties = [], assignedIds = [], onToggleProperty, compact = false,
}) {
  const setLevel = (page, level) => onPermissions({ ...permissions, [page]: level });
  return (
    <div className={compact ? "space-y-4" : "space-y-6"}>

      {/* Revenue visibility — the host's call */}
      <label className="flex items-start gap-3 p-3 rounded-xl border border-amber-200 dark:border-amber-800/40 bg-amber-50/60 dark:bg-amber-900/10 cursor-pointer">
        <input
          type="checkbox"
          checked={!!showRevenue}
          onChange={e => onShowRevenue(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded accent-amber-500"
        />
        <span className="min-w-0">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-800 dark:text-white">
            <CurrencyInr size={14} weight="bold" className="text-amber-500" /> Share revenue figures
          </span>
          <span className="block text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            When off, this person sees occupancy and bookings but no revenue, ADR, net profit, booking amounts, P&amp;L or GST report.
          </span>
        </span>
      </label>

      {/* Property assignment (co-hosts always get all properties) */}
      {role !== "co_host" && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Buildings size={15} className="text-amber-500" weight="duotone" />
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Assigned Properties</h3>
          </div>
          {properties.length === 0 ? (
            <p className="text-xs text-slate-400">No properties found.</p>
          ) : (
            <div className="space-y-1.5">
              {properties.map(p => {
                const pid = p.id || p.property_id;
                return (
                  <label key={pid} className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={assignedIds.includes(pid)}
                      onChange={() => onToggleProperty(pid)}
                      className="w-4 h-4 rounded accent-amber-500"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white">
                      {p.name}
                      {p.city && <span className="text-slate-400 text-xs ml-1">· {p.city}</span>}
                    </span>
                  </label>
                );
              })}
              <p className="text-[10px] text-slate-400 pt-1">Leave all unchecked to grant access to all properties.</p>
            </div>
          )}
        </div>
      )}

      {/* Page permissions */}
      <div>
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Page Access</h3>
        <div className="flex items-center text-[10px] font-bold text-slate-400 uppercase tracking-wide px-2 mb-1">
          <span className="flex-1">Page</span>
          <div className="flex gap-1 sm:gap-5 pr-1">
            {LEVELS.map(l => <span key={l} className="w-9 text-center capitalize">{l}</span>)}
          </div>
        </div>
        <div className="space-y-0.5">
          {ALL_PAGES.map(({ key, label }) => {
            const ownerOnly = key === "settings";
            const current = ownerOnly ? "none" : permissions[key] || "none";
            return (
              <div key={key} className="flex items-center px-2 py-1.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                <span className="flex-1 text-sm text-slate-700 dark:text-slate-300">{label}{ownerOnly && <span className="block text-xs text-slate-500 dark:text-slate-400">Host only</span>}</span>
                <div className="flex gap-1 sm:gap-5 pr-1">
                  {LEVELS.map(level => (
                    <label key={level} className="w-9 min-h-[44px] flex items-center justify-center cursor-pointer">
                      <input
                        type="radio"
                        name={`perm-${compact ? "invite" : "edit"}-${key}`}
                        value={level}
                        aria-label={`${label}: ${level === "none" ? "No access" : level === "read" ? "View only" : "View and edit"}`}
                        disabled={ownerOnly}
                        checked={current === level}
                        onChange={() => setLevel(key, level)}
                        className="accent-amber-500"
                      />
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function PermissionEditor({ member, properties = [], onClose, onSaved }) {
  const [permissions, setPermissions] = useState({});
  const [showRevenue, setShowRevenue] = useState(true);
  const [assignedIds, setAssignedIds] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const perms = member.permissions || {};
    if (Array.isArray(perms)) {
      setPermissions(Object.fromEntries(perms.map(p => [p, "edit"])));
    } else {
      setPermissions({ ...perms });
    }
    setShowRevenue(member.show_revenue ?? (member.role === "co_host"));
    setAssignedIds(member.assigned_property_ids || []);
  }, [member]);

  const toggleProperty = (id) => {
    setAssignedIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/team/members/${member.staff_id}/permissions`, {
        permissions,
        show_revenue: showRevenue,
        assigned_property_ids: assignedIds,
      });
      toast.success("Access updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg flex flex-col"
        style={{ maxHeight: "90vh" }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
          <div>
            <h2 className="text-base font-semibold text-slate-800 dark:text-white">
              Edit Access — {member.name || member.email}
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">{roleBadge(member.role)} · {member.email}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition">
            <X size={16} className="text-slate-400" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4">
          <AccessGrid
            role={member.role}
            permissions={permissions}
            onPermissions={setPermissions}
            showRevenue={showRevenue}
            onShowRevenue={setShowRevenue}
            properties={properties}
            assignedIds={assignedIds}
            onToggleProperty={toggleProperty}
          />
        </div>

        <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3 shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 text-sm font-semibold bg-amber-400 hover:bg-amber-500 disabled:opacity-50 text-white rounded-xl transition"
          >
            {saving ? "Saving…" : "Save Access"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
